import { useEffect, useMemo, useState } from "react";
import type { GameState, GeneratedMail } from "../types";
import { displayClock } from "../game/progression";
import { MailFolderList } from "../components/MailFolderList";
import { MailList } from "../components/MailList";
import { MailViewer } from "../components/MailViewer";
import { JudgmentButtons } from "../components/JudgmentButtons";
import { ProgressIndicator } from "../components/ProgressIndicator";

interface MailboxScreenProps {
  run: GameState;
  busy: boolean;
  feedback?: string;
  rewinding: boolean;
  debug: boolean;
  onJudge: (isAnomaly: boolean) => void;
  onPortal: () => void;
  onDirectory: () => void;
  onManual: () => void;
  onSettings: () => void;
}

export function MailboxScreen({ run, busy, feedback, rewinding, debug, onJudge, onPortal, onDirectory, onManual, onSettings }: MailboxScreenProps) {
  const current = run.currentMail;
  const [selected, setSelected] = useState<GeneratedMail | null>(current);
  const [folder, setFolder] = useState("inbox");

  useEffect(() => setSelected(current), [current?.variantId]);

  const selectedIsCurrent = selected?.variantId === current?.variantId;
  const uiMutation = current?.uiMutationId;
  const unread = useMemo(() => current ? 1 : 0, [current]);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (busy || !selectedIsCurrent || event.ctrlKey || event.metaKey || event.altKey) return;
      const tag = (event.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA") return;
      if (event.key.toLowerCase() === "a") onJudge(false);
      if (event.key.toLowerCase() === "d") onJudge(true);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [busy, selectedIsCurrent, onJudge]);

  if (!current || !selected) {
    return <main className="loading-screen">メールデータを読み込んでいます…</main>;
  }

  return (
    <main className={rewinding ? "mailbox-screen app-surface is-rewinding" : "mailbox-screen app-surface"}>
      <header className="mailbox-topbar">
        <div className="mailbox-brand"><strong>TO-OH WebMail</strong><span>社内専用</span></div>
        <nav>
          <button type="button" onClick={onPortal}>ポータル</button>
          <button type="button" onClick={onDirectory}>社員名簿</button>
          <button type="button" onClick={onManual}>マニュアル</button>
          <button type="button" onClick={onSettings}>設定</button>
        </nav>
        <div className="mailbox-clock"><span>2026/07/23</span><strong>{displayClock(run)}</strong></div>
      </header>

      <div className="mailbox-statusbar">
        <span>夜間監視モード</span>
        <span>判定対象: {run.currentMailIndex + 1}通目</span>
        <span className="secure-mark">● 暗号化接続</span>
      </div>

      <div className="mailbox-layout">
        <MailFolderList unreadCount={unread} uiMutationId={uiMutation} selectedFolder={folder} onSelect={setFolder} />
        {folder === "inbox" ? (
          <MailList currentMail={current} history={run.history} selectedVariantId={selected.variantId} onSelect={setSelected} />
        ) : (
          <section className="mail-list-panel empty-folder"><strong>{folder === "sent" ? (uiMutation === "folder_retired" ? "退職者" : "送信済み") : folder === "drafts" ? "下書き" : "削除済み"}</strong><p>表示できるメールはありません。</p></section>
        )}
        <section className="mail-reading-panel">
          <MailViewer mail={selected} isCurrent={selectedIsCurrent} loopCount={run.loopCount} debug={debug} />
          <JudgmentButtons disabled={busy || !selectedIsCurrent || folder !== "inbox"} finalMode={run.phase === "final"} onJudge={onJudge} />
        </section>
      </div>

      <aside className="mailbox-progress-overlay">
        <ProgressIndicator progress={run.progress} required={run.requiredProgress} loopCount={run.loopCount} />
      </aside>
      {feedback && <div className={rewinding ? "feedback-toast is-danger" : "feedback-toast"} role="status" aria-live="polite">{feedback}</div>}
    </main>
  );
}
